import { type Theme, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Swiper } from "swiper/types";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import { ELEMENT_TYPE, EXPERIENCE_PRIMARY_FILTER, EXPERIENCE_STATUS, PROPERTY_TIERS, SHARE_SCOPE } from "@/constants";
import { useAccessControl, useUrlParams } from "@/hooks";
import { useTabStatus } from "@/hooks/useTabStatus";
import { ExperienceService, ViewService } from "@/services";
import {
  getCurrentShare,
  getCurrentSharedExperience,
  removeCurrentSelectedItems,
  resetCurrentExperience,
  setSimpleValue,
} from "@/store/actions";
import { selectUser } from "@/store/auth/auth.slice";
import { useThunkDispatch, useThunkSelector } from "@/store/hooks";
import {
  currentExperienceSelector,
  currentPropertySelector,
  currentSelectedItemsSelector,
  currentSharedExperienceSelector,
  elementsSelector,
  experienceMediaItemsSelector,
  flagsSelector,
  simpleValuesSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";
import type { VisualBuilderDataState } from "@/store/types";
import { currentOrganizationSelector } from "@/store/v2/currentOrganization/currentOrganizationSlice";
import { sortMenuItems } from "@/utils/drawerUtils";
import { addExperience, addExperienceMediaItems, removeExperienceMediaItems } from "@/utils/experienceUtils.js";
import { addMediaShare, addShare, updateShare } from "@/utils/shareUtils.js";

import CollectButton from "./CollectButton";
import FullscreenCarousel from "./FullscreenCarousel";
import FullscreenGridView from "./FullscreenGridView";
import ThumbnailsCarousel from "./ThumbnailsCarousel";
import { useFullscreen } from "../../hooks/useFullscreen";
import ElementButton from "../Elements/V2/ElementButton/ElementButton.jsx";
import AudioRecording from "../Elements/V2/VoiceMemo/AudioRecording/AudioRecording";
import { handleTab } from "../GlobalTabs/GlobalTabsDataSlice";
import { changeTabStatus } from "../MainHeader/MainHeaderDataSlice";
import ShareDialog from "../Share/ShareDialog";
import ShareOptions from "../Share/components/ShareOptions";

const useStyles = makeStyles<Theme>({
  fabButtonRed: {
    backgroundColor: "#F50561 !important",

    "& svg": {
      width: 16,
      height: 16,
    },
  },
  iconButton: {
    width: 40,
    height: 40,
    padding: "8px",
    gap: "10px",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  collectButton: {
    backgroundColor: "#323232",
    "&:hover": {
      backgroundColor: "#323232",
    },

    "& svg": {
      width: 16,
      height: 16,
    },
  },
});

interface FabButtonsContainerProps {
  rightDrawer: boolean;
  carousel: boolean;
  gridView: boolean;
}

const FabButtonsContainer = styled("div")<FabButtonsContainerProps>((props) => ({
  position: "fixed",
  transition: "right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms, bottom 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
  right: props.rightDrawer ? "262px" : "22px",
  display: "flex",
  flexDirection: "column",
  alignItems: "end",
  gap: "17px",
  bottom: props.carousel && !props.gridView ? "10.5rem" : "22px",
}));

export default function Fullscreen() {
  const navigate = useNavigate();
  const dispatch = useThunkDispatch();
  const params = useUrlParams();
  const [searchParams] = useSearchParams();
  const shareQuery = searchParams.get("share");
  const { experienceId = null, propertyId, tabStatus } = params;
  const {
    isTabCollect,
    isTabCustomize,
    isTabShare,
    isTabPresent,
    isTabPublic,
    isTabPublicMediaShare,
    isTabPublicSingleShare,
    isPublicPageTab,
  } = useTabStatus();
  const { experience: sharedExperience } = useSelector(currentSharedExperienceSelector);
  const { experience: currentExperience } = useSelector(currentExperienceSelector);

  const { property } = useSelector(currentPropertySelector);
  const simpleValues = useSelector(simpleValuesSelector);
  const [thumbnailsPlay, setThumbnailsPlay] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
  const user = useThunkSelector(selectUser);
  const { id: userId } = user!;
  const { experienceMediaItems = {} } = useSelector(experienceMediaItemsSelector(propertyId, experienceId));
  const { elements } = useSelector(elementsSelector(propertyId, experienceId));
  const { canAccess } = useAccessControl();
  const shareTrigger = useSelector(() => simpleValues["experience_tab_share_trigger"]);

  const mapElement = elements.filter((el: { type: ELEMENT_TYPE }) => el.type === ELEMENT_TYPE.MAP);
  const experienceMediaItemsLength = Object.keys(experienceMediaItems).length;
  const { flags = {} } = useSelector(flagsSelector);
  const { visualBuilderData: baseVisualBuilderData } = useSelector(visualBuilderDataSelector(params)) as {
    visualBuilderData: VisualBuilderDataState & {
      assetCategoryTab: VisualBuilderDataState;
      contentTypeTab: VisualBuilderDataState;
    };
  };

  const { primaryFilter, assetCategoryTab, contentTypeTab } = baseVisualBuilderData;
  const tabVisualBuilderData = useMemo(
    () => (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE ? contentTypeTab : assetCategoryTab),
    [primaryFilter, assetCategoryTab, contentTypeTab],
  );

  const publicVisualBuilderData = useMemo(
    () => ({
      ...baseVisualBuilderData,
      ...tabVisualBuilderData,
    }),
    [baseVisualBuilderData, tabVisualBuilderData],
  );

  const visualBuilderData = useMemo(
    () => (isPublicPageTab ? publicVisualBuilderData : baseVisualBuilderData),
    [isPublicPageTab, publicVisualBuilderData, baseVisualBuilderData],
  );

  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const propertyLevel = !isOrganizationsSection;
  const {
    carousel = false,
    gridView = false,
    leftDrawerAutoHide = false,
    rightDrawerAutoHide = false,
    leftDrawer = false,
    rightDrawer = false,
    tagFilterId,
    typeFilterId,
    prevTabStatus,
    tagsToggled = {},
    currentMediaItemId,
    filteredMediaItems = [],
    propertyMenuItems = [],
    experienceMenuItems = [],
    scope: shareScope,
    locked,
    createdBy,
    mediaItems = [],
  } = visualBuilderData;

  const orderedFilteredMediaItems = useMemo(() => {
    const menuItemsToUse = experienceMenuItems.length ? experienceMenuItems : propertyMenuItems;
    if (!typeFilterId || !menuItemsToUse.length) return [...filteredMediaItems];

    return sortMenuItems({
      parentId: typeFilterId,
      itemType: "media-item",
      items: filteredMediaItems as Asset[],
      menuItems: menuItemsToUse,
    }) as Asset[];
  }, [filteredMediaItems, experienceMenuItems, propertyMenuItems, typeFilterId]);

  // Reference for the swiper instance, shared with FullscreenCarousel and ThumbnailsCarousel components.
  const swiperRef = useRef<{ swiper: Swiper } | null>(null);

  const classes = useStyles();

  const hasTierAccess = canAccess({
    allowedPermissions: ["experiences:create"],
    allowedTiers: propertyLevel ? [PROPERTY_TIERS.PRO] : [],
    extraAccessData: {
      tier: property?.tier?.title,
    },
    accessCheck: (user, extraAccessData: unknown) => {
      const { tier } = extraAccessData as { tier: string };

      if (user.isAdmin && propertyLevel) {
        return tier === PROPERTY_TIERS.PRO;
      }
      return true;
    },
  });

  const isSalesPerson = !canAccess({
    allowedPermissions: ["experiences_template_action:delete"],
    allowedTiers: [PROPERTY_TIERS.PRO],
  }) as boolean;
  const isTemplate = currentExperience?.isTemplate;

  const userCollectionAccess = canAccess({
    allowedPermissions: ["user_collection:update"],
    allowedTiers: isOrganizationsSection ? [] : [PROPERTY_TIERS.PRO, PROPERTY_TIERS.STANDARD],
  });
  const customExperienceAccess = canAccess({
    allowedPermissions: ["experiences:create"],
    allowedTiers: propertyLevel ? [PROPERTY_TIERS.PRO] : [],
  });
  const isUserCollection = currentExperience?.userCollection || false;
  const hasRightToSeeCollectButton = isUserCollection ? userCollectionAccess : customExperienceAccess;

  const currentMediaItemIndex = orderedFilteredMediaItems.findIndex((item) => item.id === currentMediaItemId);
  const currentMediaItem = orderedFilteredMediaItems[currentMediaItemIndex];
  const slideNumber = currentMediaItemIndex;
  const shareCreatedById = createdBy?.id;

  const isSingleExperienceShare = (hasTierAccess || isTabPublic) && !(isTabPublicMediaShare || isTabPublicSingleShare);

  const shouldShowMultipleShareButtons =
    (isTabPresent || isTabPublic || isTabPublicMediaShare) && mediaItems.length > 1;

  const canShowShareOptions = !gridView && mediaItems.length > 0 && !(isTabCollect || isTabCustomize || isTabShare);

  const { isReady } = useFullscreen({
    thumbnailsPlay,
    orderedFilteredMediaItems,
    currentMediaItemIndex,
    currentMediaItemId,
    gridView,
  });

  const { data: organization } = useSelector(currentOrganizationSelector);
  const organizationId = organization?.id;
  const tabPrefix = isOrganizationsSection ? `/organizations/${organizationId}` : "";

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (shareQuery === "fullCollection") {
      void onMediaShare(false);
    } else if (shareQuery === "singleAsset") {
      void onExperienceShare(false);
    }

    if (shareTrigger) {
      void onExperienceShare(shareTrigger.isSingleItem);
    }
  }, [shareQuery, isReady, simpleValues["experience_tab_share_trigger"]]);

  const { selectedItems = {} } = useSelector(currentSelectedItemsSelector);
  const selectedMediaItems = gridView ? Object.values(selectedItems) : [currentMediaItem];

  const collectedStatus = () => {
    if (isTabPresent || !experienceId || experienceMediaItemsLength === 0) {
      return true;
    }

    if (gridView) {
      return !Object.keys(selectedItems).every((key) => experienceMediaItems[key]);
    }

    const hasCurrentMediaItem = currentMediaItemId != null;
    const isNotInExperience = hasCurrentMediaItem && !experienceMediaItems[currentMediaItemId];

    return isNotInExperience;
  };

  const addNewExperience = async (
    selectedMediaItems: Asset[],
    experienceStatus: EXPERIENCE_STATUS,
    opts: Record<string, unknown>,
  ) => {
    const values: {
      title?: string;
      status?: EXPERIENCE_STATUS;
    } = {
      status: experienceStatus,
      ...(gridView !== undefined && { gridView }),
      ...(leftDrawer !== undefined && { leftDrawer }),
      ...(rightDrawer !== undefined && { rightDrawer }),
      ...(tagsToggled !== undefined && { tagsToggled }),
    };

    if (values.status === EXPERIENCE_STATUS.SINGLE_SHARE) {
      values.title = selectedMediaItems[0]?.name;
    }

    const newExperienceId = await addExperience(
      organizationId,
      `${propertyId}`,
      values,
      selectedMediaItems,
      opts,
      user!.name,
    );

    if (!newExperienceId) {
      return null;
    }

    dispatch(changeTabStatus({ tab: false }));
    dispatch(setSimpleValue("browse-experiences", false));

    if (values.status === EXPERIENCE_STATUS.DRAFT) {
      dispatch(resetCurrentExperience());
      if (currentMediaItemId) {
        await ExperienceService.updateExperience(Number(propertyId), Number(newExperienceId), {
          currentMediaItemId: currentMediaItem.id,
        });
      }
      const newState = {
        status: "active",
        name: "Loading",
      };

      const tabId = `${tabPrefix}/properties/${propertyId}/experiences/${newExperienceId}/collect`;
      dispatch(
        handleTab({
          userId,
          tabId,
          newState,
        }),
      );
      navigate(tabId);
    }

    return newExperienceId;
  };

  const fabToggle = async () => {
    if (!isTabPresent) {
      if (collectedStatus()) {
        await addExperienceMediaItems(
          `${propertyId}`,
          `${experienceId}`,
          selectedMediaItems.filter((item) => !experienceMediaItems[item.id]),
        );
      } else {
        await removeExperienceMediaItems(`${propertyId}`, `${experienceId}`, selectedMediaItems);

        if (gridView && Object.keys(selectedItems).length) {
          dispatch(removeCurrentSelectedItems(Object.keys(selectedItems)));
        }
      }
    } else {
      await addNewExperience(selectedMediaItems, EXPERIENCE_STATUS.DRAFT, {
        showToast: true,
      });
    }
  };

  const onExperienceShare = async (isSingleExperience: boolean) => {
    dispatch(setSimpleValue("share_experience", true));

    let newExperienceId = experienceId;

    if (isTabPresent) {
      newExperienceId = await addNewExperience(selectedMediaItems, EXPERIENCE_STATUS.SINGLE_SHARE, {
        showToast: false,
      });

      if (!newExperienceId) {
        return;
      }

      const values: {
        currentMediaItemId?: number;
      } = {};

      if (!gridView && currentMediaItem.id) {
        values.currentMediaItemId = currentMediaItem.id;
      }

      if (Object.keys(values).length) {
        await ExperienceService.updateExperience(Number(propertyId), Number(newExperienceId), values);
      }

      await ViewService.addView(propertyId, newExperienceId);
    } else if ((isTabCollect || isTabCustomize) && collectedStatus()) {
      await addExperienceMediaItems(`${propertyId}`, `${experienceId}`, selectedMediaItems, { showToast: false });
    }

    dispatch(getCurrentSharedExperience(propertyId, newExperienceId, userId));

    let shareOptions = {
      leftDrawer,
      rightDrawer,
      isSingleExperience,
      scope: shareScope || SHARE_SCOPE.EXPERIENCE,
    };

    if (isTabPublic) {
      shareOptions = {
        ...shareOptions,
        ...(isSingleExperience && currentMediaItemId
          ? { currentMediaItemId }
          : {
              gridView,
              ...(primaryFilter && { primaryFilter }),
              ...(typeFilterId && { typeFilterId }),
              ...(tagFilterId && { tagFilterId }),
              ...(currentMediaItemId && { currentMediaItemId }),
            }),
      };
    }

    const { shareId } = await addShare(propertyId, newExperienceId, userId, {}, shareOptions);

    if (shareId) {
      dispatch(getCurrentShare(propertyId, newExperienceId, shareId, userId));
    }

    const isPublicOrCustomize = isTabPublic || isTabCustomize;

    if (isSingleExperience && isPublicOrCustomize) {
      const options = {
        ...shareOptions,
        scope: SHARE_SCOPE.MEDIA_ITEM,
      };
      await updateShare(propertyId, experienceId, shareId, userId, { options });
    }
    shareTrigger && dispatch(setSimpleValue("experience_tab_share_trigger", null));
  };

  const onMediaShare = async (isSingleAsset: boolean) => {
    dispatch(setSimpleValue("share_without_experience", true));

    let shareOptions: {
      scope: SHARE_SCOPE;
      leftDrawer?: boolean;
      rightDrawer?: boolean;
      shareCreatedById?: number;
      currentMediaItemId?: number;
      gridView?: boolean;
    } = {
      leftDrawer,
      rightDrawer,
      scope: isSingleAsset ? SHARE_SCOPE.MEDIA_ITEM : SHARE_SCOPE.PROPERTY,
      ...(shareCreatedById && { shareCreatedById }),
    };

    const data = isSingleAsset
      ? { title: currentMediaItem.name, mediaItemId: currentMediaItemId }
      : { title: property.name };

    const isHomebaseOrPublicMediaShare = isTabPresent || isTabPublicMediaShare;

    if (!isSingleAsset && isHomebaseOrPublicMediaShare) {
      shareOptions = {
        ...shareOptions,
        gridView,
        ...(primaryFilter && { primaryFilter }),
        ...(typeFilterId && { typeFilterId }),
        ...(tagFilterId && { tagFilterId }),
        ...(currentMediaItemId && { currentMediaItemId }),
      };
    }
    try {
      const { shareId } = await addMediaShare(propertyId, userId, data, shareOptions);

      if (shareId) {
        dispatch(getCurrentShare(propertyId, null, shareId, userId));
      }

      shareTrigger && dispatch(setSimpleValue("experience_tab_share_trigger", null));
    } catch (e) {
      console.error("Error while sharing media", e);
    }
  };

  const getTopTabPadding = () => {
    if (gridView) {
      if (isTabPresent || isPublicPageTab) {
        if (orderedFilteredMediaItems.length === 0) return "";
        return "50px";
      }
      return "6rem";
    }

    if (!isTabPresent && !isPublicPageTab) {
      return "40px";
    }
    return "";
  };

  return (
    <div
      style={{
        paddingTop: getTopTabPadding(),
        zIndex: "0",
        height: "100%",
      }}
    >
      <div style={{ display: gridView && orderedFilteredMediaItems.length ? "none" : "block", height: "100%" }}>
        <FullscreenCarousel
          gridView={gridView}
          filteredMediaItems={orderedFilteredMediaItems}
          propertyId={propertyId}
          experienceId={experienceId}
          leftDrawer={leftDrawer}
          rightDrawer={rightDrawer}
          leftDrawerAutoHide={leftDrawerAutoHide}
          rightDrawerAutoHide={rightDrawerAutoHide}
          thumbsSwiper={thumbsSwiper}
          slideNumber={slideNumber}
          tabStatus={tabStatus}
          prevTabStatus={prevTabStatus}
          swiperRef={swiperRef}
          isSalesPerson={isSalesPerson}
          isTemplate={isTemplate}
        />
        <ThumbnailsCarousel
          property={property}
          currentMediaItemId={Number(currentMediaItemId)}
          filteredMediaItems={orderedFilteredMediaItems}
          setThumbsSwiper={setThumbsSwiper}
          thumbsSwiper={thumbsSwiper}
          leftDrawerAutoHide={leftDrawerAutoHide}
          rightDrawerAutoHide={rightDrawerAutoHide}
          leftDrawer={leftDrawer}
          rightDrawer={rightDrawer}
          carousel={carousel}
          setThumbnailsPlay={setThumbnailsPlay}
          thumbnailsPlay={thumbnailsPlay}
          tabStatus={tabStatus}
          swiperRef={swiperRef}
        />
      </div>
      {gridView && (
        <FullscreenGridView
          filteredMediaItems={orderedFilteredMediaItems}
          propertyId={propertyId}
          experienceId={experienceId}
          addNewExperience={addNewExperience}
          leftDrawer={leftDrawer}
          rightDrawer={rightDrawer}
          leftDrawerAutoHide={leftDrawerAutoHide}
          rightDrawerAutoHide={rightDrawerAutoHide}
          experienceMediaItems={experienceMediaItems}
          params={params}
          locked={locked || (isSalesPerson && isTemplate)}
        />
      )}
      {!!orderedFilteredMediaItems.length && (
        <FabButtonsContainer
          data-testid="fullscreen-buttons-container"
          rightDrawer={rightDrawer && !rightDrawerAutoHide}
          carousel={carousel}
          gridView={gridView}
        >
          <ElementButton
            classes={classes}
            propertyId={propertyId}
            experienceId={experienceId}
            currentMediaItemId={currentMediaItemId}
            tabStatus={tabStatus}
            gridView={gridView}
            mapElement={mapElement}
            collectedStatus={collectedStatus}
            disabled={(!!experienceId && locked) || (isSalesPerson && isTemplate)}
          />
          {/* Experiences available only for PRO tiers
            While UserCollection is available for PRO and STANDARD tiers
          */}
          {hasRightToSeeCollectButton && (
            <CollectButton
              tabStatus={tabStatus!}
              gridView={gridView}
              selectedItems={selectedItems}
              fabToggle={fabToggle}
              collectedStatus={collectedStatus}
              disabled={(!!experienceId && locked) || (isSalesPerson && isTemplate)}
            />
          )}
          {canShowShareOptions && (
            <ShareOptions
              isSingleExperienceShare={isSingleExperienceShare}
              isFullExperienceShare={isTabPublic}
              shouldShowMultipleShareButtons={shouldShowMultipleShareButtons}
              onMediaShare={onMediaShare}
              onExperienceShare={onExperienceShare}
            />
          )}
        </FabButtonsContainer>
      )}
      {(sharedExperience?.id || simpleValues["share_without_experience"]) && (
        <ShareDialog
          primaryFilter={primaryFilter}
          typeFilterId={typeFilterId}
          tagFilterId={tagFilterId}
          flags={flags}
          tabStatus={tabStatus}
          prevTabStatus={prevTabStatus}
        />
      )}

      <AudioRecording />
    </div>
  );
}
